import overEvery from 'lodash/overEvery';
import { memo, ReactNode } from 'react';
import { Navigate, RouteObject, useParams, useRoutes } from 'react-router-dom';

import featureFlagConstants from '~/common/helpers/featureFlagConstants';
import i18n from '~/common/helpers/i18n';
import useDocumentTitle from '~/common/hooks/useDocumentTitle';
import useFeatureFlag from '~/common/hooks/useFeatureFlag';
import asyncComponent from '~/components/AsyncComponent';
import { ConfirmDialogProvider } from '~/components/ConfirmDialogV2/contexts/confirmDialogProvider';
import BuildRoutes from '~/scenes/Build/scenes';
import { ConnectivityRoutes } from '~/scenes/Connectivity/scenes';
import { RouteComponent as RouteComponentV2 } from '~/scenes/Governance/scenes/Login/components';
import Organization from '~/scenes/Governance/scenes/Organization';
import LimitOfUse from '~/scenes/GrowthAndLicense/scenes/LimitOfUse';
import { MonitorRoutes } from '~/scenes/Monitor';
import Settings from '~/scenes/Others/scenes/App/Settings';
import { RoutesRun } from '~/scenes/Runtime/scenes';

const Credentials = asyncComponent(
  () => import('~/scenes/Monitor/scenes/Credentials')
);

const IdPSettings = asyncComponent(
  () => import('~/scenes/Governance/scenes/IdPSettings')
);

const AuthenticationRules = asyncComponent(
  () => import('~/scenes/Governance/scenes/AuthenticationRules')
);

const ApiKeysV2 = asyncComponent(
  () => import('~/scenes/Governance/scenes/ApiKeys')
);
const Audit = asyncComponent(() => import('~/scenes/Governance/scenes/Audit'));

const Auth = asyncComponent(
  () => import('~/scenes/IdentityAndAccess/scenes/Auth')
);

const UsersV2 = asyncComponent(
  () => import('~/scenes/Governance/scenes/Users')
);
const Globals = asyncComponent(() => import('~/scenes/Others/scenes/Globals'));
const GlobalsV2 = asyncComponent(
  () => import('~/scenes/Governance/scenes/Globals')
);

const GroupsV2 = asyncComponent(
  () => import('~/scenes/Governance/scenes/Groups')
);

const GroupsIntegrationV2 = asyncComponent(
  () => import('~/scenes/Governance/scenes/GroupsIntegration')
);
const LoginV2 = asyncComponent(
  () => import('~/scenes/Governance/scenes/Login')
);

const MultiInstanceV2 = asyncComponent(
  () => import('~/scenes/Governance/scenes/MultiInstance')
);
const DataStreaming = asyncComponent(
  () => import('~/scenes/Monitor/scenes/DataStreaming')
);
const OAuthProviders = asyncComponent(
  () => import('~/scenes/Governance/scenes/OAuthProviders')
);
const Relations = asyncComponent(
  () => import('~/scenes/Components/scenes/Relations')
);

const RolesV2 = asyncComponent(
  () => import('~/scenes/Governance/scenes/Roles')
);
const SuccessOauth2 = asyncComponent(
  () => import('~/scenes/Others/scenes/SuccessOauth2')
);
const User = asyncComponent(
  () => import('~/scenes/IdentityAndAccess/scenes/User')
);

const LicensingV2 = asyncComponent(
  () => import('~/scenes/Governance/scenes/LicenseUsage')
);

const LimitDetails = asyncComponent(
  () => import('~/scenes/GrowthAndLicense/scenes/LimitDetails')
);

const AccountsV2 = asyncComponent(
  () => import('~/scenes/Governance/scenes/Accounts')
);

const Policies = asyncComponent(
  () => import('~/scenes/Governance/scenes/Policies')
);

const TokenCtl = asyncComponent(
  () => import('~/scenes/Governance/scenes/TokenCtl')
);

const CapsuleHeader = asyncComponent(
  () => import('~/scenes/Governance/scenes/CapsuleHeaders')
);

type CustomRouteObject = RouteObject & {
  name?: string;
  title?: string;
  active?: boolean;
  key?: string;
  children?: CustomRouteObject[] | undefined;
};

type RouteValidator = (route: CustomRouteObject) => boolean;

type PageWrapperProps = {
  children?: ReactNode;
  title?: string;
};

const RouteTitleWrapper = ({ children, title }: PageWrapperProps) => {
  useDocumentTitle({ title });
  return children;
};

const traverseRoutes = (
  routes: CustomRouteObject[],
  validator: RouteValidator
): CustomRouteObject[] =>
  routes.reduce((accRoutes: CustomRouteObject[], route: CustomRouteObject) => {
    const isValid = validator(route);

    if (!isValid) return accRoutes;

    const children = route.children
      ? traverseRoutes(route.children, validator)
      : null;

    const element =
      route.element && route.title ? (
        <RouteTitleWrapper title={route.title}>
          {route.element}
        </RouteTitleWrapper>
      ) : (
        route.element
      );

    const routeWithTraversedChildren = {
      ...route,
      element,
      children
    } as CustomRouteObject;

    return accRoutes.concat(routeWithTraversedChildren);
  }, []);

type RoutesProps = {
  hiddenPages: string[];
};

const { SETTINGS_GLOBALS_V2 } = featureFlagConstants;

// eslint-disable-next-line react-refresh/only-export-components
const buildSettingsRoutes = [
  {
    path: 'capsules/headers',
    name: 'settings-capsules-headers',
    title: i18n.t('label.capsule_headers_tab'),
    element: (
      <ConfirmDialogProvider>
        <CapsuleHeader />
      </ConfirmDialogProvider>
    )
  }
];

const AccountsRedirectOrContent = () => {
  const { realm } = useParams<{ realm: string }>();
  const { treatments } = useFeatureFlag([featureFlagConstants.BUILD_ACCOUNTS]);
  const isBuildAccountsEnabled =
    treatments[featureFlagConstants.BUILD_ACCOUNTS]?.treatment === 'on';

  if (isBuildAccountsEnabled && realm) {
    return <Navigate replace to={`/${realm}/operation/build/accounts`} />;
  }
  return (
    <ConfirmDialogProvider>
      <AccountsV2 />
    </ConfirmDialogProvider>
  );
};

const GlobalsRedirectOrContent = () => {
  const { realm } = useParams<{ realm: string }>();
  const { treatments } = useFeatureFlag([
    featureFlagConstants.BUILD_GLOBALS,
    SETTINGS_GLOBALS_V2
  ]);
  const isBuildGlobalsEnabled =
    treatments[featureFlagConstants.BUILD_GLOBALS]?.treatment === 'on';
  const showGlobalsV2 = treatments[SETTINGS_GLOBALS_V2]?.treatment === 'on';

  if (isBuildGlobalsEnabled && realm) {
    return <Navigate replace to={`/${realm}/operation/build/globals`} />;
  }
  return showGlobalsV2 ? (
    <ConfirmDialogProvider>
      <GlobalsV2 />
    </ConfirmDialogProvider>
  ) : (
    <Globals />
  );
};

const Routes = ({ hiddenPages }: RoutesProps) => {
  const routesConfig: CustomRouteObject[] = [
    {
      path: '/login',
      element: <LoginV2 />,
      title: 'Login',
      children: [
        {
          index: true,
          path: '',
          title: i18n.t('label.login_tab'),
          element: <RouteComponentV2 />
        },
        {
          path: ':actionId',
          title: i18n.t('label.login_tab'),
          element: <RouteComponentV2 />
        },
        {
          path: ':actionId/:changePasswordId',
          title: i18n.t('label.change_password_tab'),
          element: <RouteComponentV2 />
        },
        {
          path: '*',
          title: i18n.t('label.login_tab'),
          element: <RouteComponentV2 />
        }
      ]
    },
    { path: '/oauth', title: i18n.t('label.oauth_tab'), element: <Auth /> },
    {
      path: '/oauth/error',
      title: i18n.t('label.oauth_tab'),
      element: <SuccessOauth2 isError />
    },
    {
      path: '/oauth2/success',
      title: i18n.t('label.oauth_tab'),
      element: <SuccessOauth2 />
    },
    ...BuildRoutes(),
    ...RoutesRun(),
    ...MonitorRoutes(),
    ...ConnectivityRoutes(),
    {
      path: '/:realm/settings',
      element: <Settings />,
      children: [
        {
          path: 'organization',
          name: 'organization',
          title: i18n.t('scenes.beta.labels.organization'),
          children: [
            {
              path: 'groups',
              title: i18n.t('scenes.beta.labels.organization_groups'),
              element: <Organization />
            }
          ]
        },
        {
          path: 'policies/:category',
          name: 'policies',
          title: 'Policies',
          element: <Policies />,
          children: [
            {
              path: ':policyName',
              title: 'policy',
              element: <Policies />
            },
            {
              path: 'compliance/pipelines/:environment',
              title: 'policy pipelines issue',
              element: <Policies />
            }
          ]
        },
        {
          path: 'beta/user',
          name: 'settings-user-beta',
          title: i18n.t('label.user_tab'),
          element: (
            <ConfirmDialogProvider>
              <UsersV2 />
            </ConfirmDialogProvider>
          )
        },
        {
          path: 'groups/list',
          name: 'settings-groups-list',
          title: i18n.t('label.groups_tab'),
          element: (
            <ConfirmDialogProvider>
              <GroupsV2 />
            </ConfirmDialogProvider>
          )
        },
        {
          path: 'groups/integrations',
          name: 'settings-groups-integrations',
          title: i18n.t('label.group_integrations_tab'),
          element: (
            <ConfirmDialogProvider>
              <GroupsIntegrationV2 />
            </ConfirmDialogProvider>
          )
        },
        {
          path: 'roles',
          name: 'settings-roles',
          title: i18n.t('label.roles_tab'),
          element: (
            <ConfirmDialogProvider>
              <RolesV2 />
            </ConfirmDialogProvider>
          )
        },
        {
          path: 'user',
          name: 'settings-user',
          title: i18n.t('label.users_tab'),
          element: <User />
        },
        {
          path: 'accounts',
          name: 'settings-accounts',
          title: i18n.t('label.accounts_tab'),
          element: <AccountsRedirectOrContent />
        },
        {
          path: 'oauthProviders',
          name: 'settings-oauth-providers',
          title: i18n.t('label.oauth_tab'),
          element: (
            <ConfirmDialogProvider>
              <OAuthProviders />
            </ConfirmDialogProvider>
          )
        },
        {
          path: 'api-keys',
          name: 'settings-api-keys',
          title: i18n.t('label.consumers_api_keys_tab'),
          element: (
            <ConfirmDialogProvider>
              <ApiKeysV2 />
            </ConfirmDialogProvider>
          )
        },
        {
          path: 'relationship',
          name: 'settings-relationship',
          title: i18n.t('label.relationship_tab'),
          element: (
            <ConfirmDialogProvider>
              <Relations />
            </ConfirmDialogProvider>
          )
        },
        {
          path: 'multiinstance',
          name: 'settings-multiinstance',
          title: i18n.t('label.multi_instance_tab'),
          element: (
            <ConfirmDialogProvider>
              <MultiInstanceV2 />
            </ConfirmDialogProvider>
          )
        },
        {
          path: 'data-streaming',
          name: 'settings-data-streaming',
          title: i18n.t('label.data_streaming_tab'),
          element: (
            <ConfirmDialogProvider>
              <DataStreaming />
            </ConfirmDialogProvider>
          )
        },
        {
          path: 'globals',
          name: 'settings-globals',
          title: i18n.t('label.globals_tab'),
          element: <GlobalsRedirectOrContent />
        },
        {
          path: 'audit',
          name: 'settings-audit',
          title: i18n.t('label.audit_tab'),
          element: (
            <ConfirmDialogProvider>
              <Audit />
            </ConfirmDialogProvider>
          )
        },
        {
          path: 'ctl/token',
          name: 'settings-ctl-token',
          title: i18n.t('label.digibeeclt_token_tab'),
          element: (
            <ConfirmDialogProvider>
              <TokenCtl />
            </ConfirmDialogProvider>
          )
        },
        ...buildSettingsRoutes,
        {
          path: 'licensing',
          name: 'settings-licensing',
          title: i18n.t('label.license_usage_tab'),
          element: <LicensingV2 />
        },
        {
          path: 'limits/global',
          name: 'settings-global-limits',
          title: i18n.t('label.global_capacity_tab'),
          element: <LimitOfUse isGlobal />,
          children: [
            {
              path: ':limit/environment/:environment',
              title: i18n.t('label.global_capacity_tab'),
              element: <LimitDetails />
            }
          ]
        },
        {
          path: 'limits/local',
          name: 'settings-local-limits',
          title: i18n.t('label.local_capacity_tab'),
          element: <LimitOfUse isGlobal={false} />,
          children: [
            {
              path: ':limit/environment/:environment',
              title: i18n.t('label.local_capacity_tab'),
              element: <LimitDetails />
            }
          ]
        },
        {
          path: 'identity-provider/authentication-rules',
          name: 'idp-authentication-rules',
          title: i18n.t('label.identity_provider_tab'),
          element: (
            <ConfirmDialogProvider>
              <AuthenticationRules />
            </ConfirmDialogProvider>
          )
        },
        {
          path: 'identity-provider/idp-settings',
          name: 'idp-settings',
          title: i18n.t('label.identity_provider_tab'),
          element: (
            <ConfirmDialogProvider>
              <IdPSettings />
            </ConfirmDialogProvider>
          )
        },
        {
          path: 'credentials',
          name: 'settings-credentials',
          title: 'Credentials',
          element: <Credentials />
        }
      ]
    }
  ];

  const isShownPage: RouteValidator = (route: CustomRouteObject) =>
    route.name ? !hiddenPages.includes(route.name) : true;

  const activeAttribute: RouteValidator = (route: CustomRouteObject) =>
    typeof route.active === 'boolean' ? route.active : true;

  const routes = traverseRoutes(
    routesConfig,
    overEvery([isShownPage, activeAttribute])
  );

  const element = useRoutes(routes);

  return element;
};

export default memo(Routes);
